// context/SocketContext.js
import { useRouter } from 'next/router';
import React from 'react';
import io, { Socket } from 'socket.io-client';
import api from 'src/api';
import { v7 as uuidv7 } from 'uuid'; // Для генерации guestId
import { from, Subject } from 'rxjs';
import { exhaustMap, delay } from 'rxjs/operators';

const SocketContext = React.createContext({
  socket: null as Socket | null,
});

export const useSocket = () => React.useContext(SocketContext);

const GUEST_ID_KEY = 'session-s-g-v-id';

type Props = {
  children: React.ReactNode;
};

const URL = process.env.NEXT_PUBLIC_API_URL || 'http://192.168.0.193:20991';

const getAccountSubject = new Subject<void>();

/**
 * Компонент провайдер для контекста сокетов
 */
export const SocketProvider = ({ children }: Props) => {
  const [socket, setSocket] = React.useState<Socket | null>(null);
  const socketRef = React.useRef<Socket | null>(null);
  const guestIdRef = React.useRef<string | null>(null);
  const currentTabRef = React.useRef<string | null>(null);

  const router = useRouter();

  const getAccountData = async () => {
    try {
      const response = await api.get('/account');
      return response.data;
    } catch {}
  };

  /**
   * Инициализация сокета при монтировании компонента
   */
  React.useEffect(() => {
    if (!socketRef.current) {
      /**
       * Генерация и сохранение уникального идентификатора гостя (guestId)
       */
      let storedGuestId = localStorage.getItem(GUEST_ID_KEY);
      if (!storedGuestId) {
        storedGuestId = uuidv7();
        localStorage.setItem(GUEST_ID_KEY, storedGuestId);
      }
      guestIdRef.current = storedGuestId;

      /**
       * Инициализация сокета
       */
      const newSocket = io(URL, {
        /**
         * Передать токен аутентификации
         */
        auth: (cb) => {
          cb({
            guestId: guestIdRef.current,
            token: localStorage.getItem('token') || '',
          });
        },
      });

      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    return () => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.disconnect();
        socketRef.current.close();
        setSocket(null);
        socketRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!currentTabRef.current) {
      /**
       * Генерация уникального идентификатора текущей вкладки
       */
      currentTabRef.current = uuidv7();
    }
    return () => {
      /**
       * Очистка идентификатора текущей вкладки при размонтировании
       */
      currentTabRef.current = null;
    };
  }, []);

  /**
   * Обработка события истечения токена
   */
  React.useEffect(() => {
    socket?.on('tokenExpired', () => {
      console.warn('Token expired, please re-authenticate');
    });
  }, [socket]);

  /**
   * Если пользователь видит страницу
   */
  React.useEffect(() => {
    const handleFocus = () => {
      socket?.emit('visibilityChange', document.visibilityState, true);
    };
    const handleBlur = () => {
      socket?.emit('visibilityChange', document.visibilityState, false);
    };

    window?.addEventListener('focus', handleFocus);
    window?.addEventListener('blur', handleBlur);
    return () => {
      window?.removeEventListener('focus', handleFocus);
      window?.removeEventListener('blur', handleBlur);
    };
  }, [socket]);
  /**
   * Если пользователь видит страницу
   */
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      socket?.emit('visibilityChange', document.visibilityState, document.hasFocus());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Отправляем начальное состояние видимости страницы
    handleVisibilityChange();
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [socket]);

  /**
   * Если пользователь переходил на другую ссылку
   */
  React.useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (socket && currentTabRef.current) {
        socket.emit('initTab', {
          url,
          tabId: currentTabRef.current,
          title: document.title,
        });
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    // Отправляем начальное состояние при монтировании
    handleRouteChange(router.asPath);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [socket, router]);

  /**
   * При подключении к сокету
   */
  React.useEffect(() => {
    const handleConnect = () => {
      socket?.emit('initTab', {
        url: router.asPath,
        tabId: currentTabRef.current,
        title: document.title,
      });

      socket?.emit('visibilityChange', document.visibilityState, document.hasFocus());
    };

    socket?.on('connect', handleConnect);

    return () => {
      socket?.off('connect', handleConnect);
    };
  }, [socket, router]);

  /**
   * При получение ивента об отзыве активности входа
   */
  React.useEffect(() => {
    /**
     * Throttle order updates to avoid excessive API calls.
     */
    const throttledOrderUpdates$ = getAccountSubject.pipe(
      exhaustMap(
        () => from(getAccountData()).pipe(delay(3000)) // Delay the next fetch by 3 seconds
      )
    );

    /**
     * Subscribe to the throttled order updates stream.
     */
    const subscription = throttledOrderUpdates$.subscribe();

    /**
     * Handle socket account update events.
     */
    const handleGetAccountUpdate = () => {
      getAccountSubject.next();
    };

    /**
     * Subscribe to the socket account update event.
     */
    socket?.on('loginActivityRevoked', handleGetAccountUpdate);

    // Cleanup
    return () => {
      subscription.unsubscribe(); // Unsubscribe from the RxJS stream
      socket?.off('loginActivityRevoked', handleGetAccountUpdate); // Remove the socket event listener
    };
  }, [socket]);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};
